(() => {
  const PRICE_KEYS = ['filter.v.price.gte', 'filter.v.price.lte'];

  function normalizePrice(raw, decimal = '.') {
    let value = String(raw).trim().replace(/[\s\u00a0\u202f]/g, '');
    if (!value) return '';
    if (!/^[\d.,]+$/.test(value)) return null;
    if (value.includes(',') && value.includes('.')) {
      const mark = value.lastIndexOf(',') > value.lastIndexOf('.') ? ',' : '.';
      value = value.replace(mark === ',' ? /\./g : /,/g, '').replace(',', '.');
    } else {
      const grouping = decimal === ',' ? '.' : ',';
      const escaped = grouping === '.' ? '\\.' : ',';
      if (new RegExp(`^\\d{1,3}(?:${escaped}\\d{3})+$`).test(value)) value = value.split(grouping).join('');
      else value = value.replace(',', '.');
    }
    if (!/^\d+(?:\.\d+)?$/.test(value)) return null;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? String(number) : null;
  }

  function buildCriteriaUrl(current, entries, decimal = '.') {
    const url = new URL(current);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || ['page', 'section_id'].includes(key)) url.searchParams.delete(key);
    }
    const grouped = new Map();
    for (const [key, raw] of entries) {
      const value = PRICE_KEYS.includes(key) ? normalizePrice(raw, decimal) : String(raw);
      if (value === null) throw new Error('invalid-price');
      if (!grouped.has(key)) grouped.set(key, []);
      if (value !== '') grouped.get(key).push(value);
    }
    for (const [key, values] of grouped) {
      url.searchParams.delete(key);
      values.forEach(value => url.searchParams.append(key, value));
    }
    url.hash = '';
    return url;
  }

  function resetFiltersUrl(current) {
    const url = new URL(current);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || ['page', 'section_id'].includes(key)) url.searchParams.delete(key);
    }
    url.hash = '';
    return url;
  }

  function sameSearchContext(a, b) {
    return a.origin === b.origin && a.pathname === b.pathname &&
      a.searchParams.get('q') === b.searchParams.get('q') && a.searchParams.get('type') === b.searchParams.get('type');
  }

  function filterSelections(url) {
    const selections = new Map();
    for (const [name, raw] of new URL(url).searchParams) {
      if (!name.startsWith('filter.') || !raw) continue;
      const values = PRICE_KEYS.includes(name) ? [raw] : raw.split(',');
      for (const value of values.filter(Boolean)) selections.set(`${name}\0${value}`, { name, value });
    }
    return Array.from(selections.values());
  }

  function removeFilterUrl(current, name, value) {
    const url = new URL(current);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    if (name === 'price') PRICE_KEYS.forEach(key => url.searchParams.delete(key));
    else {
      const remaining = filterSelections(url).filter(item => item.name === name && item.value !== value);
      url.searchParams.delete(name);
      remaining.forEach(item => url.searchParams.append(name, item.value));
    }
    return url;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalizePrice, buildCriteriaUrl, resetFiltersUrl, sameSearchContext, filterSelections, removeFilterUrl };
    return;
  }
  if (customElements.get('inventa-discovery')) return;

  class InventaDiscovery extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      this.sequence = 0;
      this.confirmedUrl = new URL(location.href);
      this.contextUrl = new URL(location.href);
      this.mediaQuery = matchMedia(window.themeVariables.breakpoints.md);
      this.onBreakpoint = () => this.placeForm();
      this.mediaQuery.addEventListener('change', this.onBreakpoint);
      window.addEventListener('popstate', () => {
        const url = new URL(location.href);
        if (!sameSearchContext(this.contextUrl, url)) { location.assign(url); return; }
        this.navigate(url, { history: false, focus: 'results' });
      }, { signal: this.lifecycle.signal });
      this.addEventListener('click', event => this.onLink(event), { signal: this.lifecycle.signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-discovery-sort] select')) this.submitCriteria(event.target);
      }, { signal: this.lifecycle.signal });
      this.bindContent();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.contentLifecycle?.abort();
      this.request?.abort();
      this.mediaQuery?.removeEventListener('change', this.onBreakpoint);
      // An open native drawer is temporarily parented to body; unload must remove it too.
      if (this.drawer && !this.contains(this.drawer)) this.drawer.remove();
    }

    async closeDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;
      if (this.closingDrawer?.drawer === drawer) return this.closingDrawer.promise;
      let promise;
      if (drawer.open) promise = Promise.resolve(drawer.hide());
      else if (drawer.parentElement === document.body) {
        // Escape/close can already be animating. Wait for native focus/DOM cleanup.
        promise = new Promise(resolve => {
          drawer.addEventListener('dialog:after-hide', resolve, { once: true });
          this.lifecycle.signal.addEventListener('abort', resolve, { once: true });
        });
      } else return;
      this.closingDrawer = { drawer, promise };
      try { await promise; } finally {
        if (this.closingDrawer?.drawer === drawer) this.closingDrawer = null;
      }
    }

    setDrawerBusy(busy) {
      if (!this.drawer) return;
      this.drawer.toggleAttribute('aria-busy', busy);
      if (busy) this.drawer.setAttribute('aria-busy', 'true');
      const pending = this.drawer.querySelector('[data-discovery-pending]');
      if (pending) {
        pending.dataset.hint ||= pending.textContent;
        pending.textContent = busy ? this.dataset.loadingMessage : pending.dataset.hint;
      }
      const apply = this.drawer.querySelector('[data-discovery-drawer-apply] button');
      if (apply) apply.disabled = busy;
      const error = this.drawer.querySelector('[data-discovery-drawer-error]');
      if (busy && error) error.hidden = true;
    }

    async bindContent(url = this.confirmedUrl) {
      this.contentLifecycle?.abort();
      this.contentLifecycle = new AbortController();
      const signal = this.contentLifecycle.signal;
      this.form = this.querySelector('[data-discovery-form]');
      this.drawer = this.querySelector('x-drawer');
      this.sidebar = this.querySelector('[data-discovery-sidebar]');
      this.hydrateUrlFilters(url);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.submitCriteria(event.submitter || document.activeElement);
      }, { signal });
      this.form.addEventListener('input', event => {
        event.target.setCustomValidity?.('');
        event.target.removeAttribute('aria-invalid');
      }, { signal });
      if (this.drawer) {
        // Drawer moves to body while open, so its links need their own listener.
        this.drawer.addEventListener('click', event => {
          if (!this.contains(this.drawer)) this.onLink(event);
        }, { signal });
        await customElements.whenDefined('x-drawer');
        if (signal.aborted || !this.isConnected) return;
        await this.placeForm();
      }
      if (!signal.aborted) this.classList.add('is-enhanced');
    }

    hydrateUrlFilters(url) {
      this.facetLabels ||= new Map();
      const groups = Array.from(this.form.querySelectorAll('[data-discovery-group]'));
      const inputs = Array.from(this.form.elements);
      for (const input of inputs) {
        if (!['checkbox', 'radio'].includes(input.type)) continue;
        const group = input.closest('[data-discovery-group]');
        const label = input.closest('.inventa-choice')?.querySelector('[data-discovery-fallback-label], .inventa-choice__label > span');
        if (label) this.facetLabels.set(`${input.name}\0${input.value}`, { label: label.textContent, group: group?.dataset.discoveryGroupLabel });
      }
      const active = this.querySelector('[data-discovery-active]');
      const selections = filterSelections(url);
      selections.forEach(({ name, value }, index) => {
        let input = inputs.find(item => item.name === name && (PRICE_KEYS.includes(name) || item.value === value));
        const group = groups.find(item => item.dataset.discoveryGroup === name);
        const remembered = this.facetLabels.get(`${name}\0${value}`);
        const valueLabel = remembered?.label || (name === 'filter.v.availability' && value === '1' ? this.dataset.availableLabel : name === 'filter.v.availability' && value === '0' ? this.dataset.unavailableLabel : value);
        const standardGroupLabel = { 'filter.v.availability': this.dataset.availabilityGroupLabel, 'filter.p.vendor': this.dataset.vendorGroupLabel, 'filter.p.product_type': this.dataset.typeGroupLabel }[name];
        const groupLabel = group?.dataset.discoveryGroupLabel || remembered?.group || standardGroupLabel;
        if (!input) {
          const template = this.form.querySelector('[data-discovery-fallback-choice]');
          if (group && template) {
            const choice = template.content.firstElementChild.cloneNode(true);
            input = choice.querySelector('input');
            input.id = `${this.form.id}-url-${index}`;
            input.name = name; input.value = value;
            choice.querySelector('label').htmlFor = input.id;
            choice.querySelector('[data-discovery-fallback-label]').textContent = valueLabel;
            choice.querySelector('.inventa-discovery__value-count').textContent = this.dataset.resultCount === '0' ? '0' : '';
            group.querySelector('fieldset').append(choice);
            group.hidden = false; group.open = true;
          } else {
            // Unsupported/missing native group: the visible chip still exposes this value.
            input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value;
            this.form.append(input);
          }
          inputs.push(input);
        }
        if (['checkbox', 'radio'].includes(input.type)) input.checked = true;
        else if (PRICE_KEYS.includes(name)) input.value = value;
        if (!active || PRICE_KEYS.includes(name)) return;
        const chips = Array.from(active.querySelectorAll('[data-discovery-remove-name]'));
        if (chips.some(chip => chip.dataset.discoveryRemoveName === name && chip.dataset.discoveryRemoveValue === value)) return;
        const template = active.querySelector('[data-discovery-fallback-chip]');
        if (!template) return;
        const item = template.content.firstElementChild.cloneNode(true), chip = item.querySelector('a');
        const label = groupLabel ? `${groupLabel}: ${valueLabel}` : valueLabel;
        chip.dataset.discoveryRemoveName = name; chip.dataset.discoveryRemoveValue = value;
        chip.href = removeFilterUrl(url, name, value).toString();
        chip.querySelector('span').textContent = label;
        chip.setAttribute('aria-label', this.dataset.removeMessage.replace('__FILTER__', label));
        active.querySelector('ul').append(item);
      });
      if (active) {
        const count = active.querySelectorAll('[data-discovery-remove-name]').length;
        active.hidden = count === 0;
        const clear = active.querySelector('[data-discovery-clear-item]');
        if (clear) {
          clear.hidden = count < 2;
          active.querySelector('ul').append(clear);
        }
      }
      const emptyTitle = this.querySelector('[data-discovery-empty-title]');
      if (selections.length && emptyTitle) {
        emptyTitle.textContent = this.dataset.emptyFilterTitle;
        this.querySelector('[data-discovery-empty-text]').textContent = this.dataset.emptyFilterText;
        this.querySelector('[data-discovery-empty-reset]').hidden = false;
        this.querySelector('[data-discovery-empty-browse]').hidden = true;
      }
    }

    async placeForm() {
      if (!this.drawer || !this.form) return;
      const revision = this.placement = (this.placement || 0) + 1;
      const field = this.form.contains(document.activeElement) ? document.activeElement : null;
      if (this.mediaQuery.matches) await this.closeDrawer();
      if (revision !== this.placement || !this.isConnected) return;
      const destination = this.mediaQuery.matches ? this.sidebar : this.drawer.querySelector('[data-discovery-drawer-mount]');
      if (this.form.parentElement !== destination) destination.append(this.form);
      if (field && this.mediaQuery.matches) field.focus({ preventScroll: true });
      else if (field && !this.mediaQuery.matches) this.querySelector('[data-discovery-filter-trigger] button')?.focus({ preventScroll: true });
    }

    decimalMark() {
      return new Intl.NumberFormat(document.documentElement.lang || undefined).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
    }

    async submitCriteria(source) {
      const decimal = this.decimalMark();
      const amounts = PRICE_KEYS.map(key => this.form.elements.namedItem(key));
      for (const input of amounts.filter(Boolean)) {
        const invalid = normalizePrice(input.value, decimal) === null;
        input.setCustomValidity(invalid ? this.form.dataset.priceError : '');
        invalid ? input.setAttribute('aria-invalid', 'true') : input.removeAttribute('aria-invalid');
      }
      const min = amounts[0] ? normalizePrice(amounts[0].value, decimal) : '';
      const max = amounts[1] ? normalizePrice(amounts[1].value, decimal) : '';
      if (min !== '' && max !== '' && min !== null && max !== null && Number(min) > Number(max)) {
        amounts[1].setCustomValidity(this.form.dataset.priceError);
        amounts[1].setAttribute('aria-invalid', 'true');
      }
      if (!this.form.reportValidity()) return;
      const url = buildCriteriaUrl(this.confirmedUrl, new FormData(this.form), decimal);
      const focus = this.drawer?.open ? 'trigger' : this.captureFocus(source);
      await this.navigate(url, { focus });
    }

    onLink(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const link = event.target.closest('a');
      if (!link || link.target || link.hasAttribute('download')) return;
      const reset = link.matches('[data-discovery-reset]') || link.closest('[data-discovery-reset]');
      if (!reset && !link.matches('[data-discovery-link], [data-discovery-page]')) return;
      const url = reset ? resetFiltersUrl(this.confirmedUrl) : link.dataset.discoveryRemoveName ? removeFilterUrl(this.confirmedUrl, link.dataset.discoveryRemoveName, link.dataset.discoveryRemoveValue) : new URL(link.href);
      if (url.origin !== location.origin) return;
      // Native remove/pagination URLs describe all criteria. Preserve preview/context parameters.
      for (const [key, value] of this.confirmedUrl.searchParams) {
        if (!key.startsWith('filter.') && !['page', 'section_id'].includes(key) && !url.searchParams.has(key)) url.searchParams.append(key, value);
      }
      event.preventDefault();
      this.navigate(url, { focus: link.hasAttribute('data-discovery-page') ? 'results' : this.drawer?.open ? 'trigger' : this.captureFocus(link) });
    }

    captureFocus(source = document.activeElement) {
      if (!source) return 'results';
      if (source.name) return { name: source.name, value: source.value, type: source.type };
      if (source.matches?.('[data-discovery-availability]')) return 'availability';
      if (source.tagName === 'A') return { href: source.getAttribute('href') };
      if (source.id) return { id: source.id };
      return 'results';
    }

    restoreFocus(intent) {
      let target;
      if (intent === 'trigger') target = this.mediaQuery.matches ? this.form.querySelector('summary, input:not([type=hidden])') : this.querySelector('[data-discovery-filter-trigger] button');
      else if (intent === 'availability') target = this.querySelector('[data-discovery-availability]');
      else if (intent?.name) target = Array.from(this.form.elements).find(input => input.name === intent.name && (!['checkbox', 'radio'].includes(intent.type) || input.value === intent.value));
      else if (intent?.id) target = this.querySelector(`#${CSS.escape(intent.id)}`);
      else if (intent?.href) target = Array.from(this.querySelectorAll('[data-discovery-link]')).find(link => link.getAttribute('href') === intent.href) || this.querySelector('.inventa-discovery__chip');
      if (!target || target.disabled || target.getClientRects().length === 0) target = this.querySelector('[data-discovery-results] h2');
      target?.focus({ preventScroll: true });
      if (intent === 'results') this.querySelector('[data-discovery-results]')?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    async navigate(nextUrl, { history: addHistory = true, focus = 'results' } = {}) {
      const url = new URL(nextUrl);
      url.searchParams.delete('section_id');
      const requestNumber = ++this.sequence;
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      this.setAttribute('aria-busy', 'true');
      this.setDrawerBusy(true);
      const status = this.querySelector('[data-discovery-status]');
      status.textContent = this.dataset.loadingMessage;
      this.querySelector('[data-discovery-error]').hidden = true;
      const openGroups = new Set(Array.from(this.form.querySelectorAll('details[open]'), group => group.id));
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(requestUrl, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('request-failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = Array.from(html.querySelectorAll('inventa-discovery')).find(item => item.dataset.sectionId === this.dataset.sectionId);
        const nextBody = next?.querySelector('[data-discovery-body]');
        if (!nextBody || !nextBody.querySelector('[data-discovery-form]') || !nextBody.querySelector('[data-discovery-results]')) throw new Error('missing-results');
        if (signal.aborted || requestNumber !== this.sequence || !this.isConnected) return;
        await this.closeDrawer();
        if (signal.aborted || requestNumber !== this.sequence || !this.isConnected) return;
        this.contentLifecycle?.abort();
        this.querySelector('[data-discovery-body]').replaceWith(document.importNode(nextBody, true));
        this.classList.toggle('inventa-discovery--has-filters', next.classList.contains('inventa-discovery--has-filters'));
        this.dataset.resultCount = next.dataset.resultCount;
        this.querySelectorAll('[data-discovery-form] details').forEach(group => group.open = openGroups.has(group.id));
        await this.bindContent(url);
        if (signal.aborted || requestNumber !== this.sequence || !this.isConnected) return;
        this.confirmedUrl = url;
        if (addHistory && url.toString() !== location.href) history.pushState({ inventaDiscovery: true }, '', url);
        status.textContent = this.querySelector('[data-discovery-count]').textContent;
        this.restoreFocus(focus);
        document.dispatchEvent(new CustomEvent('inventa:wishlist-buttons-added'));
        this.dispatchEvent(new CustomEvent('inventa:discovery-rendered', { bubbles: true, detail: { url: url.toString(), context: this.dataset.context } }));
      } catch (error) {
        if (signal.aborted || requestNumber !== this.sequence || !this.isConnected) return;
        status.textContent = '';
        const errorRegion = this.querySelector('[data-discovery-error]');
        errorRegion.querySelector('p').textContent = this.dataset.errorMessage;
        errorRegion.querySelector('a').href = url.toString();
        errorRegion.hidden = false;
        const drawerError = this.drawer?.querySelector('[data-discovery-drawer-error]');
        if (drawerError) {
          drawerError.textContent = this.dataset.errorMessage;
          drawerError.hidden = false;
        }
        // History already changed for popstate: restore a consistent server page on failure.
        if (!addHistory) location.assign(url);
      } finally {
        if (requestNumber === this.sequence && this.isConnected) {
          this.removeAttribute('aria-busy');
          this.setDrawerBusy(false);
        }
      }
    }
  }

  class InventaCollectionDescription extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-description-content]');
      this.button = this.querySelector('button');
      if (!this.content || !this.button) return;
      this.lifecycle = new AbortController();
      this.media = matchMedia(window.themeVariables.breakpoints.sm);
      this.refresh = () => {
        this.button.hidden = this.media.matches || (!this.hasAttribute('expanded') && this.content.scrollHeight <= this.content.clientHeight + 1);
      };
      this.button.addEventListener('click', () => {
        const expanded = this.toggleAttribute('expanded');
        this.button.setAttribute('aria-expanded', String(expanded));
        this.button.querySelector('span').textContent = expanded ? this.dataset.less : this.dataset.more;
        this.refresh();
      }, { signal: this.lifecycle.signal });
      this.media.addEventListener('change', this.refresh);
      this.observer = new ResizeObserver(this.refresh);
      this.observer.observe(this.content);
      this.classList.add('is-enhanced');
      this.refresh();
      document.fonts?.ready.then(() => { if (this.isConnected) this.refresh(); });
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.observer?.disconnect();
      this.media?.removeEventListener('change', this.refresh);
    }
  }
  customElements.define('inventa-discovery', InventaDiscovery);
  customElements.define('inventa-collection-description', InventaCollectionDescription);
})();
